"use client";

import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, RefreshCw, DollarSign, ArrowDownCircle, ArrowUpCircle, AlertCircle } from "lucide-react";
import DateRangePicker, { RangeWithPreset, defaultRange } from "@/components/DateRangePicker";
// InvoiceTable eliminada — no necesaria

const API = process.env.NEXT_PUBLIC_API_URL || "http://100.68.12.86:3200";

function fmt(n: number) { return (n ?? 0).toLocaleString("es-ES", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " €"; }

function VariationBadge({ v }: { v: number | null }) {
  if (v === null) return null;
  const positive = v >= 0;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: "3px",
      fontSize: "12px", fontWeight: 600, padding: "2px 8px", borderRadius: "20px",
      background: positive ? "#22c55e18" : "#ef444418",
      color: positive ? "#16a34a" : "#dc2626",
    }}>
      {positive ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
      {positive ? "+" : ""}{v}%
    </span>
  );
}

function BigCard({ label, value, variation, icon: Icon, color, dark }: any) {
  return (
    <div style={{
      background: dark ? "var(--bg-dark)" : "var(--bg-card)",
      borderRadius: "var(--radius)", border: "1px solid var(--border)",
      padding: "24px", boxShadow: "var(--shadow)",
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
        <div style={{ width: "40px", height: "40px", borderRadius: "10px", background: color + "20", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon size={18} color={color} />
        </div>
        <VariationBadge v={variation} />
      </div>
      <div style={{ fontSize: "30px", fontWeight: 700, lineHeight: 1, color: dark ? "var(--text-on-dark)" : "var(--text)" }}>{value}</div>
      <div style={{ fontSize: "13px", color: dark ? "var(--text-on-dark-muted)" : "var(--text-muted)", marginTop: "4px" }}>{label}</div>
    </div>
  );
}

function InvoiceTable({ items, title, emptyMsg, accentColor }: any) {
  return (
    <div style={{ background: "var(--bg-card)", borderRadius: "var(--radius)", border: "1px solid var(--border)", overflow: "hidden", boxShadow: "var(--shadow)" }}>
      <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: "8px" }}>
        <span style={{ fontWeight: 600, fontSize: "14px" }}>{title}</span>
      </div>
      {!items?.length ? (
        <div style={{ padding: "32px", textAlign: "center", color: "var(--text-light)", fontSize: "13px" }}>{emptyMsg}</div>
      ) : (
        <div>
          {items.map((inv: any, i: number) => (
            <div key={inv.id || i} style={{
              display: "grid", gridTemplateColumns: "1fr auto auto",
              padding: "12px 20px", borderBottom: i < items.length - 1 ? "1px solid var(--border)" : "none",
              alignItems: "center", gap: "12px",
            }}>
              <div>
                <div style={{ fontSize: "13px", fontWeight: 500 }}>{inv.contacto}</div>
                <div style={{ fontSize: "11px", color: "var(--text-light)" }}>{inv.num} · {inv.fecha}</div>
              </div>
              <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>{fmt(inv.total)}</div>
              <div style={{ fontSize: "13px", fontWeight: 600, color: accentColor }}>{fmt(inv.pendiente)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RatioRow({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderBottom: "1px solid var(--border)" }}>
      <div>
        <div style={{ fontSize: "13px", color: "var(--text)" }}>{label}</div>
        {sub && <div style={{ fontSize: "11px", color: "var(--text-light)" }}>{sub}</div>}
      </div>
      <div style={{ fontSize: "15px", fontWeight: 700 }}>{value}</div>
    </div>
  );
}

export default function FinanzasPage() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [rango, setRango] = useState<RangeWithPreset>(defaultRange());

  const load = (r: RangeWithPreset = rango, attempt = 0) => {
    setLoading(true);
    const url = `${API}/finanzas?desde=${r.desde}&hasta=${r.hasta}`;
    fetch(url).then(res => res.json()).then(d => {
      if (d.status === 'syncing' && attempt < 6) {
        setTimeout(() => load(r, attempt + 1), 5000);
        return;
      }
      setData(d);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { load(rango); }, [rango]);

  const forceSync = async () => {
    setSyncing(true);
    await fetch(`${API}/finanzas/sync`, { method: "POST" });
    setTimeout(() => { load(); setSyncing(false); }, 35000);
  };

  return (
    
      <div className="animate-fade-in" style={{ width: "100%" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "32px" }}>
          <div>
            <h1 style={{ fontSize: "28px", fontWeight: 700 }}>Finanzas</h1>
            <p style={{ color: "var(--text-muted)", marginTop: "6px" }}>
              {data?.cachedAt ? `Square + Holded · Sync: ${new Date(data.cachedAt).toLocaleTimeString("es-ES")}` : "Cargando..."}
            </p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "8px" }}>
            <button onClick={forceSync} disabled={syncing} style={{
              display: "flex", alignItems: "center", gap: "7px", padding: "9px 16px",
              background: "var(--bg-muted)", border: "1px solid var(--border)", borderRadius: "10px",
              fontSize: "13px", cursor: "pointer", color: "var(--text-muted)", opacity: syncing ? 0.6 : 1,
            }}>
              <RefreshCw size={13} className={syncing ? "animate-spin" : ""} />
              {syncing ? "Sincronizando..." : "Sincronizar"}
            </button>
            <DateRangePicker value={rango} onChange={(r) => setRango(r)} />
          </div>
        </div>

        {loading && !data ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "80px", gap: "12px", color: "var(--text-muted)" }}>
            <RefreshCw size={20} className="animate-spin" />
            <span>Descargando datos de Square y Holded...</span>
          </div>
        ) : !data || data.error ? (
          <div style={{ textAlign: "center", padding: "80px", color: "var(--text-light)" }}>
            <AlertCircle size={40} style={{ margin: "0 auto 16px", display: "block", opacity: 0.2 }} />
            <p>Error cargando datos. <button onClick={() => load()} style={{ color: "var(--accent)", background: "none", border: "none", cursor: "pointer" }}>Reintentar</button></p>
          </div>
        ) : (
          <>
            {/* Banner aviso gastos mes anterior */}
            {data.gastosNota && (
              <div style={{ background: "#f59e0b15", border: "1px solid #f59e0b40", borderRadius: "10px", padding: "10px 16px", marginBottom: "16px", display: "flex", alignItems: "center", gap: "10px", fontSize: "13px", color: "#f59e0b" }}>
                <span>⚠️</span>
                <span>{data.gastosNota}</span>
              </div>
            )}

            {/* KPIs */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px", marginBottom: "24px" }}>
              <BigCard label="Ingresos del mes" value={fmt(data.ingresos)} variation={data.varIngresos} icon={ArrowUpCircle}   color="#22c55e" />
              <BigCard label={data.gastosNota ? "Gastos (mes anterior)" : "Gastos del mes"} value={fmt(data.gastos)} variation={data.varGastos} icon={ArrowDownCircle} color="#ef4444" />
              <BigCard label="Margen bruto"     value={`${fmt(data.margen)} (${data.margenPct}%)`} variation={null} icon={DollarSign} color="#C9A84C" dark />
            </div>

            {/* Proyección + Ratios */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "24px" }}>

              {/* Proyección */}
              <div style={{ background: "var(--bg-card)", borderRadius: "var(--radius)", border: "1px solid var(--border)", padding: "24px", boxShadow: "var(--shadow)" }}>
                <div style={{ fontSize: "14px", fontWeight: 600, marginBottom: "16px" }}>Proyección mensual</div>
                {data.proyeccion && (() => {
                  const { ingresosHoy, objetivoMes, proyeccionMes, pctObjetivo, diaActual, diasEnMes } = data.proyeccion;
                  const pctDias = Math.round((diaActual / diasEnMes) * 100);
                  return (
                    <>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
                        <div><div style={{ fontSize: "22px", fontWeight: 700 }}>{fmt(ingresosHoy)}</div><div style={{ fontSize: "12px", color: "var(--text-muted)" }}>Acumulado</div></div>
                        <div><div style={{ fontSize: "22px", fontWeight: 700 }}>{fmt(proyeccionMes)}</div><div style={{ fontSize: "12px", color: "var(--text-muted)" }}>Proyección</div></div>
                      </div>
                      {objetivoMes > 0 && <>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: "var(--text-muted)", marginBottom: "6px" }}>
                          <span>Objetivo: {fmt(objetivoMes)}</span><span>{pctObjetivo}%</span>
                        </div>
                        <div style={{ height: "6px", background: "var(--bg-muted)", borderRadius: "6px" }}>
                          <div style={{ height: "100%", width: `${Math.min(pctObjetivo || 0, 100)}%`, background: (pctObjetivo || 0) >= pctDias ? "#22c55e" : "#f59e0b", borderRadius: "6px" }} />
                        </div>
                        <div style={{ fontSize: "11px", color: "var(--text-light)", marginTop: "6px" }}>Día {diaActual} de {diasEnMes}</div>
                      </>}
                    </>
                  );
                })()}
              </div>

              {/* Ratios */}
              <div style={{ background: "var(--bg-card)", borderRadius: "var(--radius)", border: "1px solid var(--border)", padding: "24px", boxShadow: "var(--shadow)" }}>
                <div style={{ fontSize: "14px", fontWeight: 600, marginBottom: "4px" }}>Ratios operativos</div>
                <RatioRow label="Promedio diario"     value={fmt(data.ratios?.promedioDiario)}                                   />
                <RatioRow label="Ticket medio"        value={fmt(data.ratios?.ticketMedio)}                                      />
                <RatioRow label="Coste de ventas"     value={`${data.ratios?.costeVentasPct}%`}     sub="gastos / ingresos"      />
                <RatioRow label="Prime Cost est."     value={`${data.ratios?.primeCostPct}%`}       sub="mat. prima + personal"  />
              </div>
            </div>

            {/* Facturas por pagar */}
            <div style={{ background: "var(--bg-card)", borderRadius: "var(--radius)", border: "1px solid var(--border)", overflow: "hidden", boxShadow: "var(--shadow)", marginBottom: "24px" }}>
              <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontWeight: 600, fontSize: "14px" }}>🔴 Facturas por pagar</span>
                <span style={{ fontSize: "13px", fontWeight: 700, color: "#ef4444" }}>{fmt(data.totalPorPagar)}</span>
              </div>
              {!data.porPagar?.length ? (
                <div style={{ padding: "40px", textAlign: "center", color: "var(--text-light)", fontSize: "13px" }}>Sin facturas pendientes de pago</div>
              ) : (
                <div style={{ maxHeight: "480px", overflowY: "auto" }}>
                  {/* Header */}
                  <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", padding: "10px 24px", borderBottom: "1px solid var(--border)", fontSize: "11px", color: "var(--text-light)", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", background: "var(--bg-muted)" }}>
                    <span>Proveedor</span>
                    <span style={{ textAlign: "right" }}>Fecha</span>
                    <span style={{ textAlign: "right" }}>Total</span>
                    <span style={{ textAlign: "right" }}>Pendiente</span>
                  </div>
                  {data.porPagar.map((inv: any, i: number) => (
                    <div key={inv.id || i} style={{
                      display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr",
                      padding: "14px 24px",
                      borderBottom: i < data.porPagar.length - 1 ? "1px solid var(--border)" : "none",
                      alignItems: "center",
                      background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
                    }}>
                      <div>
                        <div style={{ fontSize: "13px", fontWeight: 500, color: "var(--text)" }}>{inv.contacto}</div>
                        <div style={{ fontSize: "11px", color: "var(--text-light)", marginTop: "2px" }}>Nº {inv.num}</div>
                      </div>
                      <div style={{ fontSize: "12px", color: "var(--text-muted)", textAlign: "right" }}>
                        {inv.fecha.slice(5).replace("-", "/")}
                      </div>
                      <div style={{ fontSize: "13px", color: "var(--text-muted)", textAlign: "right" }}>{fmt(inv.total)}</div>
                      <div style={{ fontSize: "14px", fontWeight: 700, color: "#ef4444", textAlign: "right" }}>{fmt(inv.pendiente)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Top proveedores */}
            <div style={{ background: "var(--bg-card)", borderRadius: "var(--radius)", border: "1px solid var(--border)", padding: "24px", boxShadow: "var(--shadow)", marginBottom: "24px" }}>
              <div style={{ fontSize: "14px", fontWeight: 600, marginBottom: "16px" }}>Top proveedores{data.gastosNota ? " — mes anterior" : " — gastos del mes"}</div>
              {data.topProveedores?.length === 0 && <div style={{ color: "var(--text-light)", fontSize: "13px" }}>Sin datos de Holded</div>}
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {data.topProveedores?.map((p: any, i: number) => {
                  const max = data.topProveedores[0]?.total || 1;
                  const pct = Math.round((p.total / max) * 100);
                  return (
                    <div key={i}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: "3px" }}>
                        <span>{p.nombre}</span>
                        <span style={{ fontWeight: 600 }}>{fmt(p.total)}</span>
                      </div>
                      <div style={{ height: "4px", background: "var(--bg-muted)", borderRadius: "4px" }}>
                        <div style={{ height: "100%", width: `${pct}%`, background: "#ef4444", borderRadius: "4px", opacity: 0.7 }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>


          </>
        )}
      </div>
    
  );
}
